import { call, put, select, takeLatest } from 'redux-saga/effects';

import authService from 'services/authService';
import teamService from 'services/teamService';

import * as SESSION from 'store/session';

import history from 'utils/browserHistory';

import * as actions from './actions';
import { resendInvite } from './actions';
import * as selectors from './selectors';
import * as types from './types';

export function* getTeamMembers(apiParams) {
  try {
    const groupId = yield select(SESSION.selectors.getGroupId);
    const response = yield call(teamService.getTeamMembers, groupId, apiParams);
    yield put(actions.getTeamMembers.success({ teamMembers: response }));
  } catch (error) {
    yield put(actions.getTeamMembers.error({ error }));
  }
}

function* watchGetTeamMembers() {
  yield call(getTeamMembers);
}

export function* getTeamMember(teamMemberId) {
  try {
    const response = yield call(teamService.getTeamMember, teamMemberId);
    yield put(actions.getTeamMember.success({ teamMember: response }));
    yield put(
      actions.checkEmailVerification.request({ teamMemberId: response.userId }),
    );
  } catch (error) {
    yield put(actions.getTeamMember.error({ error }));
  }
}

function* watchDeleteTeamMember(action) {
  try {
    const { teamMemberId } = action.payload;
    yield call(teamService.deleteTeamMember, teamMemberId);
    yield put(actions.deleteTeamMember.success({ teamMemberId }));
  } catch (error) {
    yield put(actions.deleteTeamMember.error({ error }));
  }
}

function* watchCreateTeamMember(action) {
  try {
    const { teamMember } = action.payload;
    const groupId = yield select(SESSION.selectors.getGroupId);
    const response = yield call(
      teamService.inviteTeamMember,
      groupId,
      teamMember,
    );
    history.push('/team');
    yield put(actions.createTeamMember.success({ response }));
  } catch (error) {
    yield put(actions.createTeamMember.error({ error }));
  }
}

function* watchResendTeamMemberInvite(action) {
  try {
    const groupId = yield select(SESSION.selectors.getGroupId);
    const { emailAddress } = action.payload;
    const response = yield call(resendTeamMemberInvite, {
      groupId,
      emailAddress,
    });
    yield put(resendInvite.success({ response }));
    yield call(getTeamMembers);
  } catch (error) {
    yield put(resendInvite.error(error));
  }
}

function* resendTeamMemberInvite({ groupId, emailAddress }) {
  return yield teamService.invites.resend(groupId, {
    email_address: emailAddress,
  });
}

function* watchUpdateTeamMember(action) {
  try {
    const { teamMember, cb } = action.payload;
    const response = yield call(
      teamService.updateTeamMember,
      teamMember.id,
      teamMember,
    );
    const user = yield select(SESSION.selectors.getUser);
    if (teamMember.email === user.email) {
      const user = yield call(authService.profile);
      yield put(SESSION.actions.receiveUser(user));
    }
    yield put(actions.updateTeamMember.success({ teamMember: response }));
    cb && cb();
  } catch (error) {
    yield put(actions.updateTeamMember.error({ error }));
  }
}

function* watchSendVerificationEmail() {
  try {
    const { userId } = yield select(selectors.getActiveTeamMember);
    yield call(teamService.sendVerificationEmail, userId);
    yield put(actions.sendVerificationEmail.success());
  } catch (error) {
    yield put(actions.sendVerificationEmail.error({ error }));
  }
}

export function* watchCheckEmailVerification() {
  try {
    const { userId } = yield select(selectors.getActiveTeamMember);
    const isVerified = yield call(teamService.getIsEmailVerified, userId);
    yield put(actions.checkEmailVerification.success({ isVerified }));
  } catch (error) {
    yield put(actions.checkEmailVerification.error({ error }));
  }
}

export default function* root() {
  yield takeLatest(types.GET_TEAM_MEMBERS_REQUEST, watchGetTeamMembers);
  yield takeLatest(types.DELETE_TEAM_MEMBER_REQUEST, watchDeleteTeamMember);
  yield takeLatest(types.CREATE_TEAM_MEMBER_REQUEST, watchCreateTeamMember);
  yield takeLatest(types.UPDATE_TEAM_MEMBER_REQUEST, watchUpdateTeamMember);
  yield takeLatest(
    types.TEAM_MEMBER_INVITE_RESEND_REQUEST,
    watchResendTeamMemberInvite,
  );
  yield takeLatest(
    types.SEND_VERIFICATION_EMAIL_REQUEST,
    watchSendVerificationEmail,
  );
  yield takeLatest(
    types.CHECK_EMAIL_VERIFICATION_REQUEST,
    watchCheckEmailVerification,
  );
}
